/**
 * KeyFieldsGrid - Reusable 2-column grid for entity key fields.
 *
 * For CREATED/DELETED: renders all fields with values.
 * For UPDATED: renders changed fields normally, unchanged fields
 * are contributed to the parent's "show more" section via the
 * unchangedContent render pattern.
 *
 * Simplified version without transfer checkboxes.
 */

import type { EntityChangeType } from '@/services/api/generated/entityChangesService';

import type { ChangedFieldsInfo } from '../utils/useChangedFields';
import { EntityField } from './EntityField';

export interface FieldConfig {
  key: string;
  label: string;
  isDate?: boolean;
  isEnum?: boolean;
}

interface KeyFieldsGridProps {
  fields: FieldConfig[];
  data: Record<string, unknown>;
  changes: ChangedFieldsInfo;
  changeType?: EntityChangeType;
}

function renderFieldGrid(
  fields: FieldConfig[],
  data: Record<string, unknown>,
  changes: ChangedFieldsInfo,
) {
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
      {fields.map((f) => (
        <div key={f.key} className="flex items-start">
          <div className="flex-1 min-w-0">
            <EntityField
              label={f.label}
              value={data[f.key]}
              fieldKey={f.key}
              changes={changes}
              isDate={f.isDate}
              isEnum={f.isEnum}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export const KeyFieldsGrid: React.FC<KeyFieldsGridProps> = ({
  fields,
  data,
  changes,
  changeType,
}) => {
  const visibleFields = fields.filter((f) => {
    const val = data[f.key];
    const hasValue = val !== null && val !== undefined && val !== '';
    return hasValue || changes.isFieldChanged(f.key);
  });

  if (visibleFields.length === 0) return null;

  // For CREATED/DELETED (or no changeType), show everything
  const showAll =
    !changeType || changeType === 'created' || changeType === 'deleted';

  if (showAll) {
    return renderFieldGrid(visibleFields, data, changes);
  }

  // For UPDATED/IGNORED: only render changed fields here
  const changedFields = visibleFields.filter((f) =>
    changes.isFieldChanged(f.key),
  );

  if (changedFields.length === 0) return null;

  return renderFieldGrid(changedFields, data, changes);
};

/**
 * KeyFieldsGridUnchanged - Renders only the unchanged fields from a KeyFieldsGrid.
 *
 * Use this inside a "Show More" collapsible to display unchanged fields
 * for UPDATED entities.
 */
export const KeyFieldsGridUnchanged: React.FC<KeyFieldsGridProps> = ({
  fields,
  data,
  changes,
  changeType,
}) => {
  // For CREATED/DELETED, KeyFieldsGrid already renders all fields — nothing extra needed
  if (!changeType || changeType === 'created' || changeType === 'deleted') {
    return null;
  }

  const unchangedFields = fields.filter((f) => {
    const val = data[f.key];
    const hasValue = val !== null && val !== undefined && val !== '';
    return hasValue && !changes.isFieldChanged(f.key);
  });

  if (unchangedFields.length === 0) return null;

  return renderFieldGrid(unchangedFields, data, changes);
};
